import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Hexagon } from "lucide-react";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1 -mb-1">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const [count, setCount] = useState(null);
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrapRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-48, 48]);
  const rotate = useTransform(scrollYProgress, [0, 1], [2.5, -1.5]);

  useEffect(() => {
    api.waitlistCount().then((r) => setCount(r.count)).catch(() => {});
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative overflow-hidden hex-pattern" data-testid="hero-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <motion.p
            className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#D97706] font-semibold"
            data-testid="hero-overline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          >
            Family Apiary · Hand-Poured Goods
          </motion.p>
          <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2C221E] leading-[1.1]" data-testid="hero-heading">
            <MaskedLine delay={0.3}>A little sweet.</MaskedLine>
            <MaskedLine delay={0.48} className="italic text-[#B45309]">A little wild.</MaskedLine>
          </h1>
          <motion.p
            className="mt-6 text-base sm:text-lg text-[#5A4D44] leading-relaxed max-w-xl"
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          >
            McGee's Bees began with our 16-year-old son, two hives, and a whole lot of curiosity. His passion
            has grown into a family side job we all love — handcrafting beeswax candles, lip balms, soaps, and
            lotions while we wait for our very first honey harvest in <strong className="text-[#2C221E]">Spring 2027</strong>.
          </motion.p>
          <motion.div
            className="mt-8 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          >
            <button
              onClick={() => scrollTo("shop")}
              data-testid="hero-shop-button"
              className="px-7 py-3.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-semibold text-sm transition-all hover:gap-3 active:scale-[0.97] flex items-center gap-2"
            >
              Shop Handcrafted Goods <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollTo("honey")}
              data-testid="hero-waitlist-button"
              className="px-7 py-3.5 rounded-full border-2 border-[#2C221E] text-[#2C221E] hover:bg-[#2C221E] hover:text-[#FAF5EE] font-semibold text-sm transition-colors active:scale-[0.97]"
            >
              Join the 2027 Honey Waitlist
            </button>
          </motion.div>
          <motion.div
            className="mt-10 flex flex-wrap gap-6"
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
          >
            {[
              ["2", "Active Hives"],
              ["100%", "Pure Beeswax"],
              ["2027", "First Honey Harvest"],
            ].map(([num, label]) => (
              <div key={label} className="flex items-center gap-2.5">
                <Hexagon className="w-5 h-5 text-[#D97706]" fill="#F3EBE0" />
                <div className="leading-tight">
                  <span className="font-serif-display font-bold text-lg text-[#2C221E] block">{num}</span>
                  <span className="text-xs text-[#8C7A6B]">{label}</span>
                </div>
              </div>
            ))}
          </motion.div>
          {count !== null && count > 0 && (
            <p className="mt-5 text-xs font-mono-accent uppercase tracking-[0.15em] text-[#B45309]" data-testid="hero-waitlist-count">
              {count} {count === 1 ? "neighbor has" : "neighbors have"} joined the honey waitlist
            </p>
          )}
        </div>

        <div className="md:col-span-5" ref={imgWrapRef}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
          >
            <motion.div style={{ y, rotate }} className="relative will-change-transform">
              <div className="absolute -inset-3 bg-[#F3EBE0] rounded-[2rem] rotate-2" />
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_mcgees-honey--1e0t67ze/artifacts/b3b74f402d0ad61c_e4dc15f2-5cf9-44b7-b42b-b7d5fe92ae5c.png"
                alt="A Naturally Golden Glow — McGee's Bees natural beeswax candles"
                className="relative rounded-[2rem] w-full object-cover shadow-xl"
                data-testid="hero-product-image"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
